import { useState, useContext, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useContactForm = () => {
  const { api, seoApi, seobixApi } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);

  const postRequest = useCallback(
    async (endpoint, body) => {
      const response = await api.post(endpoint, body);
      return response?.data;
    },
    [api],
  );

  const seoPostRequest = useCallback(
    async (endpoint, body) => {
      const response = await seoApi.post(endpoint, body);
      return response?.data;
    },
    [seoApi],
  );

  const seobixPostRequest = useCallback(
    async (endpoint, body) => {
      const response = await seobixApi.post(endpoint, body);

      return response?.data;
    },
    [seobixApi],
  );

  const submitContactForm = async (formData) => {
    setLoading(true);

    try {
      const payload = {
        userid: "",
        step: "1",
        step1: {
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          workEmail: formData.workEmail,
          message: formData.message,
        },
      };

      const data = await postRequest("/User/v1.0/updateUserDetails", payload);

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Something went wrong",
      };
    } finally {
      setLoading(false);
    }
  };

  const submitForm = async (formData) => {
    setLoading(true);

    try {
      const payload = {
        fullname: formData.fullName,
        email: formData.workEmail,
        website: formData.website || "",
        contact: formData.phoneNumber,
      };

      const data = await seoPostRequest("/Auth/InsertLeads", payload);

      if (data?.status === "0") {
        return {
          success: true,
          message: data?.result?.[0]?.message || "Form submitted successfully",
        };
      }

      return {
        success: false,
        message: "Submission failed. Please try again.",
      };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Something went wrong",
      };
    } finally {
      setLoading(false);
    }
  };

  const submitEnquiryDetails = async (formData) => {
    setLoading(true);

    try {
      const payload = {
        full_name: formData.fullName || "",
        email: formData.email || "",
        enquiry_type: formData.enquiryType || "",
        description: formData.description || "",
      };

      const data = await postRequest(
        "/User/v1.0/InsertEnquiryDetails",
        payload,
      );

      return {
        success: true,
        data,
        message: data?.responseMessage || "Enquiry submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      };
    } finally {
      setLoading(false);
    }
  };

 const submitRestaurantBasicInfo = async (formData) => {
   if (!seobixApi || typeof seobixApi.post !== "function") {
     console.error("seobixApi instance is not available:", seobixApi);

     return {
       success: false,
       message: "API configuration is missing.",
     };
   }

   setLoading(true);

   try {
     const payload = {
       name: String(formData?.name || "").trim(),
       email: String(formData?.email || "").trim(),
       phone: String(formData?.phone || "").trim(),
       restaurant_name: String(formData?.restaurantName || "").trim(),
       latitude: String(formData?.latitude || "28.624711238077882"),
       longitude: String(formData?.longitude || "77.3717769111169"),
     };

     const response = await seobixApi.post(
       "/Order/insertRestaurantBasicInfo",
       payload,
       {
         headers: {
           "Content-Type": "application/json",
           Accept: "application/json",
         },
       },
     );

     const data = response?.data;

     if (String(data?.responseCode) === "00") {
       return {
         success: true,
         data,
         message:
           data?.response?.[0]?.message ||
           data?.responseMessage ||
           "Inserted successfully",
       };
     }

     return {
       success: false,
       data,
       message:
         data?.responseMessage ||
         data?.response?.[0]?.message ||
         "Submission failed",
     };
   } catch (error) {
     console.error("submitRestaurantBasicInfo error:", error);

     return {
       success: false,
       message:
         error?.response?.data?.responseMessage ||
         error?.response?.data?.message ||
         error?.message ||
         "Something went wrong",
     };
   } finally {
     setLoading(false);
   }
 };

const getOrganizationTypes = useCallback(async () => {
  try {
    const data = await postRequest("/Admin/GetOrganizationTypeAdmin", {});

    if (data?.responseCode === "00") {
      return {
        success: true,
        data: Array.isArray(data?.response) ? data.response : [],
      };
    }

    return {
      success: false,
      data: [],
      message: data?.responseMessage || "Unable to fetch organization types",
    };
  } catch (error) {
    console.error("getOrganizationTypes error:", error);

    return {
      success: false,
      data: [],
      message:
        error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        "Unable to fetch organization types",
    };
  }
}, [postRequest]);

  return {
    submitContactForm,
    submitForm,
    submitEnquiryDetails,
    submitRestaurantBasicInfo,
    getOrganizationTypes,
    loading,
  };
};

export default useContactForm;
